import * as psl from "psl";

import { slugify } from "./string";

export function isValidHttpUrl(url: string) {
  const validUrl = normalizeUrlIncludingProtocolRelative(url);
  if (!validUrl) {
    return false;
  }
  return validUrl.protocol === "http:" || validUrl.protocol === "https:";
}

/**
 * Tries to parse and validate the given string as a URL, including protocol-relative
 * URLs. Returns a URL if parsing is possible, otherwise null.
 *
 * Note: Javascript URLs don't recognize protocol-relative URLs (e.g. //google.com)
 * as valid, so we need special casing here. If protocol-relative, the returned
 * url will have an https protocol. This default is arbitrary but backed up by best
 * practice: https://webhint.io/docs/user-guide/hints/hint-no-protocol-relative-urls/
 */
export function normalizeUrlIncludingProtocolRelative(url: string): URL | null {
  let validUrl;
  try {
    validUrl = new URL(url);
  } catch {
    if (url.startsWith("//")) {
      try {
        validUrl = new URL(`https:${url}`);
      } catch {
        return null;
      }
    } else {
      return null;
    }
  }
  return validUrl;
}

/**
 * Validates domain format and checks against publicly listed suffixes.
 */
export function isValidDomain(domain: string): boolean {
  const domainRegex =
    /^(?:[\dA-Za-z](?:[\dA-Za-z-]*[\dA-Za-z])?\.)+[A-Za-z]{2,}$/;
  if (!domainRegex.test(domain)) {
    return false;
  }

  // Validate TLD is in Public Suffix List
  const parsed = psl.parse(domain);
  if ("error" in parsed) {
    return false;
  }

  return parsed.listed === true;
}

/**
 * Use PSL to determine if a domain is root (e.g., example.com, example.com.au)
 * or a subdomain (e.g., www.example.com, shop.example.com.au).
 *
 * Note: psl.parse() returns a union type that can be either a ParsedDomain
 * (success) or ErrorResult (failure). We must properly narrow the type before
 * accessing domain/subdomain properties.
 */
export function isRootDomain(domain: string): boolean {
  const parsed = psl.parse(domain);

  // psl.parse() returns ParsedDomain | ErrorResult
  // ErrorResult has an 'error' property, ParsedDomain has a 'domain' property
  if ("error" in parsed) {
    return false;
  }

  return Boolean(parsed.domain && parsed.subdomain === null);
}

export function normalizeUrlScheme(url: string) {
  return url.startsWith("//") ? `https:${url}` : url;
}

/**
 * Truncates a domain name by shortening the subdomain portion while preserving
 * the main domain suffix. Useful for displaying long domain names in UI.
 *
 * @param domain - The domain to truncate (e.g., "very-long-subdomain.example.com")
 * @param maxLength - Maximum length of the truncated domain (default: 40)
 * @returns Truncated domain with ellipsis (e.g., "very-long-sub...example.com")
 */
export function truncateDomain(domain: string, maxLength = 60): string {
  // If domain is short enough, return as-is
  if (domain.length <= maxLength) {
    return domain;
  }

  // Split the domain into parts
  const parts = domain.split(".");

  // Need at least 2 parts (subdomain.domain)
  if (parts.length < 2) {
    return domain.length <= maxLength
      ? domain
      : `${domain.slice(0, maxLength - 3)}...`;
  }

  // For common patterns like *.reploshops.com, preserve the last 2 parts
  // For regular domains, preserve the last 2 parts (domain.tld)
  const mainDomainParts = parts.slice(-2);
  const subdomainParts = parts.slice(0, -2);

  const mainDomain = mainDomainParts.join(".");

  // If even just the main domain is too long, truncate it
  if (mainDomain.length >= maxLength - 3) {
    return `${mainDomain.slice(0, maxLength - 3)}...`;
  }

  // Calculate available space for subdomain
  const availableSpace = maxLength - mainDomain.length - 4; // 4 for "..." and "."

  if (subdomainParts.length === 0) {
    return domain;
  }

  const subdomainString = subdomainParts.join(".");

  if (subdomainString.length <= availableSpace) {
    return domain;
  }

  // Truncate subdomain portion
  const truncatedSubdomain = subdomainString.slice(
    0,
    Math.max(1, availableSpace),
  );

  return `${truncatedSubdomain}...${mainDomain}`;
}

/**
 * Validates that a route path is properly formatted and URL-safe.
 * Routes must start with / and contain only valid slug segments.
 *
 * @param path - The route path to validate
 * @returns true if the path is a valid route, false otherwise
 */
export const isValidRoutePath = (path: string): boolean => {
  // Must start with a slash
  if (!path.startsWith("/")) {
    return false;
  }

  // Remove leading slash and split by remaining slashes
  const segments = path.slice(1).split("/");

  // Empty path (just "/") is valid
  if (segments.length === 1 && segments[0] === "") {
    return true;
  }

  // Each segment must be a valid slug
  return segments.every((segment) => {
    if (segment === "") {
      return false;
    }
    return segment === slugify(segment) && segment.length > 0;
  });
};
